import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/sonner";
import { EchoProvider } from "@/providers/EchoProvider";
import { EmailVerificationModal } from "@/components/shared/EmailVerificationModal";
import { MaintenanceOverlay } from "@/components/shared/MaintenanceOverlay";
import { ErrorFallback } from "@/components/shared/ErrorFallback";
import { QueryIdentityGuard } from "@/components/shared/QueryIdentityGuard";
import { AppVersionGate } from "@/components/shared/AppVersionGate";
import { AddToHomeScreenPopup } from "@/components/shared/pwa/AddToHomeScreenPopup";
import { PushEnablePrompt } from "@/components/shared/push/PushEnablePrompt";
import { APP_BUILD_ID } from "@/lib/app-version";
import { router } from "@/router";
import { ApiError, markPageUnloading } from "@/services/api";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import { formatReference, isChunkLoadError, isPreloadFetchError } from "@/lib/error-kind";
import { initSentry } from "@/lib/sentry";
import { isLiveConnectionDegraded } from "@/lib/live-connection";
import { useUiStore } from "@/store/ui.store";
import "./styles/index.css";

function DynamicToaster() {
  const position = useUiStore((s) => s.toasterPosition);
  return <Toaster position={position} richColors closeButton />;
}

/**
 * Polyfill de `Promise.withResolvers` (ES2024, absent avant Safari 17.4 / iOS 17.4).
 *
 * `pdfjs-dist` s'appuie dessus depuis sa v4 — jusque dans le champ d'instance de
 * `PDFDocumentLoadingTask`, donc dès le `new`, en dehors de tout `try/catch` de la librairie.
 * Sans lui, ouvrir un PDF plantait immédiatement sur les iPhone encore en iOS 16
 * (`TypeError: undefined is not a function`, SUCCESCHOOL-FRONTEND-V2-1Y) — hors de portée du
 * prop `error` de `<Document>`, qui ne rattrape que les échecs de chargement/parsing, pas un
 * throw synchrone à la construction. Posé ici plutôt que localement dans `PdfViewer` : c'est
 * une lacune de l'environnement, pas du composant, et `pdfjs.getDocument()` n'est jamais le
 * seul point d'appel possible dans le temps (worker, réutilisations futures).
 */
if (typeof (Promise as { withResolvers?: unknown }).withResolvers !== "function") {
  (
    Promise as unknown as {
      withResolvers: <T>() => {
        promise: Promise<T>;
        resolve: (value: T | PromiseLike<T>) => void;
        reject: (reason?: unknown) => void;
      };
    }
  ).withResolvers = function withResolvers<T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

/**
 * Polyfill de `URL.parse` (2024 — absent avant Firefox 126, Chrome 126 et Safari 18.4).
 *
 * Même famille que le polyfill ci-dessus, et même origine : `pdfjs-dist` l'appelle dans
 * `getUrlProp`, atteint synchroniquement depuis `getDocument()`. Le throw est donc hors de portée
 * du prop `error` de `<Document>`, qui ne rattrape que les échecs de chargement — l'écran entier
 * tombait sur l'ErrorBoundary à la simple ouverture d'un PDF.
 *
 * Population concernée : Firefox 115 ESR, dernière version supportée sur Windows 7, encore en
 * service chez des familles (SUCCESCHOOL-FRONTEND-V2-2K).
 *
 * Sémantique de la spec : renvoie une `URL` ou `null` là où le constructeur lève. La coercition en
 * chaîne reproduit celle du constructeur — `pdfjs` passe `window.location` comme base, un objet
 * `Location` et non une chaîne.
 */
if (typeof (URL as { parse?: unknown }).parse !== "function") {
  (URL as unknown as { parse: (url: unknown, base?: unknown) => URL | null }).parse =
    function parse(url: unknown, base?: unknown): URL | null {
      try {
        return base == null ? new URL(String(url)) : new URL(String(url), String(base));
      } catch {
        return null;
      }
    };
}

initSentry();

// TEMPORAIRE (debug audio) — console à l'écran pour lire les logs sur mobile/iPad sans câble.
// S'active en visitant n'importe quelle page avec `?debug=1`, puis reste active pour l'onglet.
// À retirer une fois le debug terminé.
function loadEruda() {
  try {
    // Accéder à `sessionStorage` lève un `SecurityError` sur Safari quand le stockage est
    // bloqué (réglages de confidentialité stricts, certains contextes de navigation privée) —
    // appelée directement au chargement du module, une exception ici empêchait React de monter
    // et laissait une page entièrement blanche (SUCCESSCHOOL-FRONTEND-V2-21). Un outil de debug
    // ne doit jamais pouvoir bloquer le démarrage de l'app.
    const params = new URLSearchParams(window.location.search);
    if (params.has("debug")) sessionStorage.setItem("eruda", "1");
    if (sessionStorage.getItem("eruda") !== "1") return;
  } catch {
    return;
  }
  const script = document.createElement("script");
  script.src = "https://cdn.jsdelivr.net/npm/eruda";
  script.onload = () => {
    (window as unknown as { eruda?: { init: () => void } }).eruda?.init();
  };
  document.body.appendChild(script);
}
loadEruda();

// TEMPORAIRE (debug déploiement) — badge de version visible sur toutes les pages, même en prod,
// pour vérifier d'un coup d'œil qu'on est sur le dernier build déployé. À retirer ensuite.
const APP_VERSION = APP_BUILD_ID.slice(0, 7);

function mountVersionBadge() {
  const badge = document.createElement("div");
  badge.textContent = APP_VERSION;
  badge.style.cssText =
    "position:fixed;bottom:4px;left:4px;z-index:2147483647;font:10px/1.4 ui-monospace,monospace;" +
    "color:#fff;background:rgba(0,0,0,.6);padding:1px 6px;border-radius:4px;pointer-events:none;" +
    "opacity:.65;white-space:nowrap";
  document.body.appendChild(badge);
}
mountVersionBadge();

/**
 * Écart minimal entre deux rechargements provoqués par un chunk introuvable.
 *
 * Assez long pour qu'un échec qui se reproduit immédiatement — coupure réseau pendant le
 * rechargement, serveur qui ne sert toujours pas les bons fichiers — ne parte pas en boucle. Assez
 * court pour qu'un onglet resté ouvert plusieurs jours, dont les chunks ont disparu entre-temps,
 * retrouve un filet plutôt que de rester sur un écran vide.
 */
const CHUNK_RELOAD_MIN_INTERVAL_MS = 10 * 60 * 1000;

// After a new deployment, old chunk URLs (content-hashed) no longer exist on the
// server. Vite fires this event when a dynamic import 404s; reloading fetches the
// new index.html and resolves the correct chunk URLs.
// sessionStorage guard prevents infinite reload loops (e.g. network outage during reload).
window.addEventListener("vite:preloadError", () => {
  try {
    /**
     * Garde-fou **daté**, et non plus définitif.
     *
     * Le drapeau posé une fois n'était jamais effacé : un onglet ayant connu un seul incident de
     * chargement perdait son filet pour toute sa durée de vie. Sur iOS, cette durée se compte en
     * jours — un élève tournait encore le 24/08 sur le build du 13, dont tous les chunks avaient
     * été effacés par les déploiements suivants. Nginx répondait `index.html` à chaque import
     * différé, le navigateur recevait du HTML là où il attendait du JavaScript, et l'écran restait
     * vide sans qu'aucun rechargement ne puisse plus survenir (SUCCESCHOOL-FRONTEND-V2-4S).
     *
     * La garde protégeait d'une boucle de quelques secondes et le payait par un blocage de onze
     * jours. Un horodatage garde la première propriété — deux échecs rapprochés ne donnent qu'un
     * rechargement — sans la seconde : un épisode de péremption survenu bien plus tard retrouve son
     * filet, puisque c'est un incident distinct et non la boucle qu'on redoute.
     */
    const lastReloadAt = Number(sessionStorage.getItem(STORAGE_KEYS.CHUNK_RELOAD_AT));
    const isRecent =
      Number.isFinite(lastReloadAt) && Date.now() - lastReloadAt < CHUNK_RELOAD_MIN_INTERVAL_MS;
    if (!isRecent) {
      sessionStorage.setItem(STORAGE_KEYS.CHUNK_RELOAD_AT, String(Date.now()));
      // Même raison que dans `reloadNow` : ce rechargement annule les requêtes en vol, et sans ce
      // marquage chacune remonterait comme une coupure réseau alors que c'est nous qui coupons.
      markPageUnloading();
      window.location.reload();
    }
  } catch {
    // Stockage bloqué : impossible de poser le garde-fou anti-boucle, donc pas de rechargement
    // automatique ici (un échec persistant, pas seulement post-déploiement, boucle à l'infini
    // sans lui). `RouteErrorBoundary` prend le relais avec un « Réessayer » manuel.
  }
});

/**
 * Décrit une raison de rejet qui n'est pas une `Error`.
 *
 * `String(reason)` sur un événement DOM produit « [object Event] » : toute l'information est
 * perdue, et **tous** les échecs de transport se regroupent dans une seule issue Sentry
 * indistincte, sans rien qui permette de les départager. C'est exactement ce qui est arrivé aux
 * coupures WebSocket de la visioconférence, restées inexploitables pendant plusieurs semaines de
 * signalements clients alors que la trace existait.
 *
 * On extrait donc ce qui identifie l'échec : son type, le service concerné, et l'état de la
 * connexion au moment où il survient — de quoi distinguer un échec d'établissement d'une rupture
 * en cours de session.
 */
function describeRejection(reason: unknown): Error {
  if (reason instanceof Error) return reason;

  if (typeof Event !== "undefined" && reason instanceof Event) {
    const parts = [`Event:${reason.type}`];
    const target = reason.target;

    if (typeof WebSocket !== "undefined" && target instanceof WebSocket) {
      // Jamais l'URL complète : celle de LiveKit porte le jeton d'accès en paramètre de requête,
      // qui n'a rien à faire dans un outil de suivi d'erreurs. L'hôte suffit à identifier le
      // service, et `readyState` dit à quel moment de la vie de la connexion l'échec survient.
      let host = "inconnu";
      try {
        host = new URL(target.url).host;
      } catch {
        // URL inattendue : l'hôte n'apporterait rien de fiable, on garde le reste.
      }
      /**
       * `online` sépare deux pannes que `readyState` confond.
       *
       * Au moment où l'erreur remonte, le socket est fermé dans tous les cas : `readyState` vaut 3
       * et ne distingue rien. Le SDK, lui, sait s'il se croyait connecté — il ne déclenche sa
       * reprise que dans ce cas — mais cet état est interne à son client signal et n'apparaît nulle
       * part sur l'`Event`.
       *
       * `navigator.onLine` ne reproduit pas exactement cette distinction, il en tranche une autre,
       * aussi utile : hors ligne, c'est la connexion de l'élève qui a lâché et il n'y a rien à
       * chercher côté service ; en ligne, un socket mort pendant que le réseau répond est le cas
       * intéressant — celui du `STATE_MISMATCH` où le transport s'éteint sans que rien ne le
       * signale. Son défaut connu joue ici du bon côté : `false` est fiable, `true` peut être
       * optimiste, donc le doute retombe toujours sur la population qu'on veut examiner.
       *
       * Ces événements sont regroupés par message, faute d'empreinte explicite : les ajouter le
       * scinde donc en deux issues, ce qui est précisément l'objectif.
       */
      const online = typeof navigator === "undefined" ? "inconnu" : String(navigator.onLine);
      parts.push(`ws=${host}`, `readyState=${target.readyState}`, `online=${online}`);
    }

    if (typeof CloseEvent !== "undefined" && reason instanceof CloseEvent) {
      parts.push(`code=${reason.code}`, `wasClean=${String(reason.wasClean)}`);
    }

    return new Error(parts.join(" "));
  }

  // `DOMException` (rejet d'un `AbortController`, refus d'API navigateur…) échoue à
  // `instanceof Error` sur certains moteurs, et porte `name`/`message` sur son prototype plutôt
  // qu'en propriété propre énumérable — invisibles à `JSON.stringify`, qui produit alors "{}" et
  // perd toute information exploitable (constaté en prod, régression du même genre que celle
  // décrite ci-dessus pour les `Event`). `in` traverse la chaîne de prototypes, contrairement à
  // `JSON.stringify` : on détecte donc ces objets par leur forme plutôt que par leur type.
  if (reason && typeof reason === "object" && ("message" in reason || "name" in reason)) {
    const name = "name" in reason && typeof reason.name === "string" ? reason.name : "Error";
    const message = "message" in reason && typeof reason.message === "string" ? reason.message : "";
    const err = new Error(message ? `${name}: ${message}` : name);
    err.name = name;
    return err;
  }

  try {
    return new Error(typeof reason === "string" ? reason : JSON.stringify(reason));
  } catch {
    // Objet non sérialisable (cyclique, getter qui jette) : le repli vaut mieux que rien.
    return new Error(String(reason));
  }
}

// Une promesse rejetée sans `catch` ne remontait nulle part : ni console utile, ni Sentry.
// C'est typiquement ce qui se cache derrière un écran qui « ne fait rien » ou reste vide.
window.addEventListener("unhandledrejection", (event) => {
  const error = describeRejection(event.reason);
  /**
   * Annulation demandée — même règle que dans la couche API (`isAbortError`) : ce n'est pas un
   * incident, c'est quelqu'un qui a coupé court volontairement.
   *
   * Le SDK LiveKit en produit à chaque reprise de connexion : `Room.connect()` avorte le contrôleur
   * de la tentative précédente avant d'en ouvrir une (`connectFn`), et tout ce qui attendait ce
   * signal rejette avec le motif par défaut de WebKit — « AbortError: The operation was aborted. »,
   * sans la moindre pile puisque `DOMException` n'en porte pas. C'est SUCCESCHOOL-FRONTEND-V2-2B :
   * un événement qui ne dit ni d'où il vient ni ce qu'il empêche, sur une salle qui, elle, était
   * restée connectée — le fil d'Ariane le dit, « already connected to room ».
   *
   * Le test porte sur l'erreur décrite plutôt que sur `event.reason` : `describeRejection` recopie
   * le `name` du rejet d'origine, là où `instanceof Error` ne suffirait pas à reconnaître une
   * `DOMException` sur tous les moteurs.
   */
  if (error.name === "AbortError" || error.name === "TimeoutError") return;

  /**
   * Refus que la couche API a délibérément tu (`silenced` : règle métier 422 expliquée par le
   * backend, e-mail non vérifié…) — elle n'a créé aucune issue parce qu'il n'y a rien à corriger.
   * Un appelant sans `catch` le renvoyait ici et recréait l'issue qu'elle venait d'éviter
   * (« Le motif est obligatoire pour une absence justifiée », constaté le 18/10).
   */
  if (error instanceof ApiError && error.silenced) return;

  /**
   * Négociation avortée **parce que la connexion vient de lâcher** : le SDK lance une négociation,
   * le signal meurt (« ping timeout », « signal disconnected »), et la promesse en attente expire
   * quinze secondes plus tard sur son délai fixe (« negotiation timed out »). C'est la même coupure
   * vue du côté du transport, pas un second incident — et celle-ci est déjà tracée par la
   * machinerie de déconnexion, qui ne la signale que si elle n'est pas rattrapée.
   *
   * Le test se fait sur le `name` plutôt que `instanceof NegotiationError` : importer le SDK ici le
   * ferait entrer dans le bundle initial. La reprise reste décidée par `LiveClassRoom`, qui écoute
   * ce même rejet ; seul le signalement est écarté.
   *
   * Un échec survenu sur une salle **connectée** (UDP filtré, NAT, pare-feu) n'est pas concerné et
   * reste signalé : c'est celui-là qui porte de l'information (SUCCESCHOOL-FRONTEND-V2-F).
   */
  if (error.name === "NegotiationError" && isLiveConnectionDegraded()) {
    Sentry.addBreadcrumb({
      category: "livekit",
      level: "info",
      message: `${error.message} — consécutif à une coupure de connexion`,
    });
    return;
  }

  if (isPreloadFetchError(error)) {
    Sentry.captureException(error, {
      tags: { source: "unhandledrejection", error_kind: "preload-fetch-failure" },
      fingerprint: ["preload-fetch-failure"],
      level: "warning",
    });
    return;
  }

  /**
   * Même échec qu'un `lazy()` de route (RouteErrorBoundary), mais parti d'un `import()` ad hoc
   * hors routeur — `loadFirebase()` (`firebase/messaging`) notamment — qui rejette sans jamais
   * passer par `vite:preloadError` ni un `errorElement`. Sans ce test il atterrissait ici en
   * simple rejet non catégorisé (« error », non groupé) plutôt que dans le même regroupement
   * « chunk-load-failure » que les autres chunks disparus après déploiement.
   */
  if (isChunkLoadError(error)) {
    Sentry.captureException(error, {
      tags: { source: "unhandledrejection", error_kind: "chunk-load-failure" },
      fingerprint: ["chunk-load-failure"],
      level: "warning",
    });
    return;
  }

  Sentry.captureException(error, {
    tags: { source: "unhandledrejection" },
  });
});

const queryClient = new QueryClient({
  /**
   * Filet de sécurité pour les échecs de lecture, et **seulement** ceux que personne n'a vus passer.
   *
   * Sans `onError` global, une lecture en échec n'était tracée que si l'écran pensait à le faire —
   * donc quasiment jamais. Mais capturer ici *tout* échec doublait chaque appel d'API raté : une
   * fois par `fetchJson` (méthode, route normalisée, statut, une issue par endpoint), une fois ici,
   * pour la même cause. Ce filet ne se déclenche donc que sur ce que la couche API n'a pas rapporté.
   *
   * Rien ne se perd à ce tri. La `query_key` disait « quel écran » ; l'événement d'origine le dit
   * déjà mieux, via les tags `transaction` et `url` que Sentry pose sur chaque événement — la route
   * réelle plutôt que le nom d'un cache — et il dit en plus quel endpoint a échoué et comment.
   *
   * Restent visés par ce filet : ce qui casse *dans* la `queryFn` après une réponse pourtant reçue
   * (forme inattendue, transformation qui lève). Là, la clé de requête est bien le seul repère.
   *
   * Pas de toast : les erreurs de lecture sont déjà rendues à l'écran par `ErrorState`.
   */
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Le 401 déclenche déjà une déconnexion + redirection : ce n'est pas un incident.
      if (error instanceof ApiError && error.status === 401) return;
      // Déjà rapporté par la couche API (`eventId`), ou délibérément tu par elle (`silenced` :
      // page qui s'en va, appareil hors ligne). Dans les deux cas la décision est déjà prise, et
      // la refaire ici ne produirait qu'un doublon — ou annulerait un silence voulu.
      if (error instanceof ApiError && (error.eventId !== undefined || error.silenced)) return;
      Sentry.captureException(error, {
        tags: { source: "react-query", query_key: String(query.queryKey[0] ?? "unknown") },
        extra: { queryKey: query.queryKey },
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min
      // `retry: 1` retentait aussi les 4xx (droits, validation, ressource absente), qui ne
      // peuvent pas réussir au second essai : ça ne faisait que doubler l'attente avant
      // l'affichage de l'erreur. On ne retente que ce qui est susceptible de passer.
      //
      // 429 en est délibérément exclu : le quota (300 req/min partagé par utilisateur, tous
      // endpoints confondus) ne se régénère pas dans la fenêtre d'un retry immédiat, donc retenter
      // ne fait qu'ajouter une requête au compteur déjà épuisé — transformant un 429 isolé en deux
      // (SUCCESCHOOL-FRONTEND-V2, issue 143026681).
      retry: (failureCount, error) => {
        if (failureCount >= 1) return false;
        if (error instanceof ApiError) {
          if (error.status === 408) return true;
          return error.status === 0 || error.status >= 500;
        }
        return true;
      },
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    // Fallback en fonction (et non en élément statique) pour récupérer l'`eventId` et
    // afficher la même référence que partout ailleurs.
    fallback={({ error, eventId }) => (
      <ErrorFallback
        fullScreen
        error={error instanceof Error ? error : null}
        reference={formatReference(eventId)}
      />
    )}
  >
    <QueryClientProvider client={queryClient}>
      <QueryIdentityGuard />
      <EchoProvider>
        <RouterProvider router={router} />
        <DynamicToaster />
        <EmailVerificationModal />
        <MaintenanceOverlay />
        <AppVersionGate />
        <AddToHomeScreenPopup />
        <PushEnablePrompt />
      </EchoProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>,
);
